
/**
 * Utilities focused on optimizing Largest Contentful Paint (LCP)
 */

/**
 * Cache for preloaded LCP images
 */
const lcpImageCache = new Map<string, HTMLImageElement>();

/**
 * Detect if user is on a mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         (window.innerWidth < 768);
}

/**
 * Detect if user is on a slow connection
 */
export function isSlowConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  // Check for Network Information API
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;
    
    if (conn) {
      // If saveData is true, user has requested reduced data usage
      if (conn.saveData) return true;
      
      // Check connection type
      if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return true;
      
      // Check if bandwidth is below threshold (1.5Mbps)
      if (typeof conn.downlink === 'number' && conn.downlink < 1.5) return true;
    }
  }
  
  return false;
}

/**
 * Preload an image specifically for LCP optimization
 * @param src Image source URL
 * @returns Promise that resolves when image is loaded
 */
export function preloadLcpImage(src: string): Promise<HTMLImageElement> {
  // For mobile, use a smaller image size to reduce load time
  const optimizedSrc = isMobileDevice() ? optimizeImageForMobile(src) : src;
  
  // First, create a preload link for browser resource prioritization
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizedSrc;
  link.fetchPriority = 'high';
  document.head.appendChild(link);

  // Then actually preload the image to get it into the browser cache
  return new Promise((resolve, reject) => {
    // Check cache first
    if (lcpImageCache.has(optimizedSrc)) {
      resolve(lcpImageCache.get(optimizedSrc)!);
      return;
    }

    const img = new Image();
    img.fetchPriority = 'high';
    img.decoding = isMobileDevice() ? 'async' : 'sync'; // Use async for mobile to reduce main thread work
    img.src = optimizedSrc;
    
    img.onload = () => {
      lcpImageCache.set(optimizedSrc, img);
      
      // Report loading metrics
      if (performance && performance.mark) {
        performance.mark(`lcp-image-loaded-${optimizedSrc.split('/').pop()}`);
      }
      
      resolve(img);
    };
    
    img.onerror = () => {
      console.error(`Failed to preload LCP image: ${optimizedSrc}`);
      reject(new Error(`Failed to preload LCP image: ${optimizedSrc}`));
    };
  });
}

/**
 * Optimize image URL for mobile devices
 */
function optimizeImageForMobile(src: string): string {
  if (!src) return src;
  
  try {
    // For Unsplash images, add size and quality parameters
    if (src.includes('unsplash.com')) {
      if (src.includes('?')) {
        return `${src}&w=640&q=70&auto=format&fit=crop`;
      } else {
        return `${src}?w=640&q=70&auto=format&fit=crop`;
      }
    }
    
    // For local images, we can't resize them dynamically
    // But we can make sure mobile-specific images are used
    if (src.includes('og-image') && !src.includes('mobile')) {
      return src.replace('og-image.png', 'og-image-mobile.png');
    }
    
    return src;
  } catch (e) {
    console.warn('Error optimizing image for mobile', e);
    return src;
  }
}

/**
 * Initialize LCP optimization
 * This should be called as early as possible in the application lifecycle
 */
export function initLcpOptimization(): void {
  // Create LCP observer to report metrics
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        // Log LCP metrics
        console.log('LCP detected:', lastEntry.startTime.toFixed(2), 'ms');
        console.log('LCP element:', (lastEntry as any).element); // Use type assertion for element
        
        // Report to performance monitoring
        if (performance && performance.mark) {
          performance.mark('lcp-detected');
          performance.measure('lcp-to-app-start', 'app-start', 'lcp-detected');
          console.log('LCP time:', lastEntry.startTime);
        }
      });
      
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      console.log('LCP observer initialized');
    } catch (e) {
      console.error('Error setting up LCP observer:', e);
    }
  }
  
  // Set up font optimization
  if (typeof document !== 'undefined') {
    // Create a style element instead of trying to set fontDisplay directly
    const style = document.createElement('style');
    style.textContent = `
      * {
        font-display: swap !important;
      }
      
      /* Mobile-specific optimizations */
      @media (max-width: 767px) {
        /* Reduce animations for better performance */
        * {
          transition-duration: 0.1s !important;
          animation-duration: 0.1s !important;
          animation-delay: 0s !important;
        }
        
        /* Optimize text rendering */
        body {
          text-rendering: optimizeSpeed;
          text-size-adjust: none;
        }
        
        /* Optimize image rendering for mobile */
        img {
          content-visibility: auto;
        }
        
        /* Reduce layout complexity */
        .container {
          padding-left: 12px !important;
          padding-right: 12px !important;
        }
        
        /* Improve touch targets */
        button, a {
          min-height: 44px;
          min-width: 44px;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Add mobile viewport optimization
    if (isMobileDevice()) {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover');
      }
    }
  }
  
  // Mobile-specific optimizations
  if (typeof document !== 'undefined' && isMobileDevice()) {
    // Delay non-critical resources on mobile
    const delayNonCriticalResources = () => {
      // Find non-critical images (not marked as LCP)
      document.querySelectorAll('img:not([data-lcp="true"])').forEach(img => {
        // Use loading lazy for better performance
        // Use type assertion to HTMLImageElement
        (img as HTMLImageElement).loading = 'lazy';
        // Lower priority to prioritize critical resources
        // Use type assertion to add fetchPriority
        (img as any).fetchPriority = 'low';
      });
      
      // Defer non-critical scripts
      document.querySelectorAll('script:not([data-critical="true"])').forEach(script => {
        if (script.getAttribute('src') && !script.getAttribute('async') && !script.getAttribute('defer')) {
          script.setAttribute('defer', '');
        }
      });
      
      // Apply content-visibility to off-screen elements
      document.querySelectorAll('.container > div:not(.home-hero-element)').forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.contentVisibility = 'auto';
          el.style.containIntrinsicSize = '1px 400px';
        }
      });
    };
    
    // Run after initial render
    if (document.readyState === 'complete') {
      delayNonCriticalResources();
    } else {
      window.addEventListener('load', delayNonCriticalResources);
    }
    
    // Apply progressive hydration simulation
    window.addEventListener('DOMContentLoaded', () => {
      // Prioritize rendering of above-the-fold content
      const prioritizeAboveTheFold = () => {
        const viewport = window.innerHeight;
        document.querySelectorAll('img').forEach(img => {
          const rect = img.getBoundingClientRect();
          if (rect.top < viewport * 1.5) {
            // Above the fold or just below - load immediately
            (img as HTMLImageElement).loading = 'eager';
          } else {
            // Well below the fold - lazy load
            (img as HTMLImageElement).loading = 'lazy';
          }
        });
      };
      
      requestAnimationFrame(prioritizeAboveTheFold);
    });
  }
  
  // Detect and report render-blocking resources
  if ('PerformanceObserver' in window) {
    try {
      const blockingObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries.length > 0) {
          console.warn('Render-blocking resources detected:', entries.length);
          entries.forEach(entry => {
            console.warn(`- ${entry.name.split('/').pop()}: ${entry.duration.toFixed(2)}ms`);
          });
        }
      });
      
      blockingObserver.observe({ type: 'resource', buffered: true });
    } catch (e) {
      console.error('Error setting up blocking resources observer:', e);
    }
  }
}

// Initialize right away when this module is imported
if (typeof window !== 'undefined') {
  initLcpOptimization();
}
